<template>
  <div data-pw="base-summary">
    <SummaryBlock
      :icon="isDelegated ? 'iconUser' : 'iconCal'"
      :title="isDelegated ? 'Termin bei' : 'Unser Terminangebot'"
    >
      <BaseText>{{ nameLine }}</BaseText>
      <BaseText>{{ addressLine }}, {{ cityLine }}</BaseText>
      <BaseText>{{ timeslot.day }}, {{ timeslot.time }} Uhr</BaseText>
      <BaseText>Termindauer: 25 Minuten</BaseText>
    </SummaryBlock>
    <SummaryBlock
      v-if="shouldShowActivities"
      icon="iconWench"
      title="Geplante Arbeiten"
    >
      <BaseText>{{ activities }}</BaseText>
      <Spacer v-if="video" :mt="2" />
      <BaseLink v-if="video" @click="toggleVideoModal(true)">
        Informationen zu den Arbeiten ansehen
      </BaseLink>
    </SummaryBlock>
    <BaseSummaryTechnician
      v-if="shouldShowTechnician"
      :appointment="appointment"
      @click="clickDLPush('technikerfoto-anzeigen'),
              toggleTechnicianModal(true)"
    />
    <BaseModal
      :active="videoModalIsOpen"
      button-label="Schließen"
      @closed="toggleVideoModal(false)"
    >
      <template #content>
        <ModalVideoContent v-if="videoModalIsOpen" :active="videoModalIsOpen" />
      </template>
    </BaseModal>
    <BaseModal
      :active="technicianModalIsOpen"
      button-label="Schließen"
      data-pw="modal-technician"
      @closed="clickDLPush('technikerfoto-schließen'),
               toggleTechnicianModal(false)"
    >
      <template #content>
        <ModalTechnicianContent v-if="technicianModalIsOpen" />
      </template>
    </BaseModal>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { isEmpty } from 'lodash-es'
import BaseModal from '~/base/components/modal/Modal.vue'
import ModalVideoContent from '~/terminierungsportal/components/modals/ModalVideoContent.vue'
import ModalTechnicianContent from '~/terminierungsportal/components/modals/ModalTechnicianContent.vue'
import type {
  Appointment,
  FormattedTimeslot,
  Video
} from '~/terminierungsportal/types/appointmentTypes'
import SummaryBlock from '~/terminierungsportal/components/summary/BaseSummaryBlock.vue'
import Spacer from '~/base/components/spacer/Spacer.vue'
import BaseLink from '~/base/components/link/Link.vue'
import BaseText from '~/base/components/text/Text.vue'
import BaseSummaryTechnician from '~/terminierungsportal/components/summary/BaseSummaryTechnician.vue'
import { useDataLayerMixin } from '~/base/composables/useDataLayer'

interface BaseSummaryProps {
  appointment: Appointment
  timeslot: FormattedTimeslot
}

const { appointment, timeslot } = defineProps<BaseSummaryProps>()

const videoModalIsOpen = ref(false)
const technicianModalIsOpen = ref(false)
const { clickDLPush } = useDataLayerMixin()

const video = computed((): Video | null => {
  return appointment.video
})

const isDelegated = computed((): boolean => {
  return appointment.isDelegated
})

const nameLine = computed((): string => {
  const { firstName, lastName } = appointment.contactInfo
  return `${firstName} ${lastName}`
})

const addressLine = computed((): string => {
  const { street, houseNr } = appointment.location
  return `${street} ${houseNr}`
})

const cityLine = computed((): string => {
  const { postalCode, city } = appointment.location
  return `${postalCode} ${city}`
})

const activities = computed((): string => {
  return appointment.activities
})

const shouldShowActivities = computed((): boolean => {
  return !isEmpty(appointment.activities)
})

const shouldShowTechnician = computed((): boolean => {
  return (
    !isEmpty(appointment.technician) &&
    !isEmpty(appointment.technician.picture) &&
    !isEmpty(appointment.technician.profilePicFaceBox)
  )
})

const toggleVideoModal = (state: boolean): void => {
  videoModalIsOpen.value = state
}

const toggleTechnicianModal = (state: boolean): void => {
  technicianModalIsOpen.value = state
}
</script>
